import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2,
  ExternalLink,
  Landmark,
  LoaderCircle,
  RefreshCw,
  Unplug,
} from "lucide-react";

import {
  beginQuickBooksConnection,
  disconnectQuickBooks,
  getQuickBooksStatus,
  testQuickBooksConnection,
} from "./api";
import type { QuickBooksStatus } from "./types";

export function QuickBooksPage({ canManage }: { canManage: boolean }) {
  const [status, setStatus] = useState<QuickBooksStatus>();
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<"connect" | "test" | "disconnect">();
  const [message, setMessage] = useState(callbackMessage);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await getQuickBooksStatus());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function connect() {
    setAction("connect");
    setFailed(false);
    try {
      window.location.assign(await beginQuickBooksConnection());
    } catch {
      setFailed(true);
      setAction(undefined);
    }
  }

  async function testConnection() {
    setAction("test");
    setFailed(false);
    try {
      const result = await testQuickBooksConnection();
      setMessage(
        result.companyName
          ? `Connection verified for ${result.companyName}.`
          : "Connection verified.",
      );
      await load();
    } catch {
      setFailed(true);
    } finally {
      setAction(undefined);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect QuickBooks and revoke TLC's current authorization?")) return;
    setAction("disconnect");
    setFailed(false);
    try {
      await disconnectQuickBooks();
      setMessage("QuickBooks was disconnected and its stored tokens were removed.");
      await load();
    } catch {
      setFailed(true);
    } finally {
      setAction(undefined);
    }
  }

  return (
    <div className="page-content quickbooks-page">
      <header className="page-intro integration-intro">
        <span className="eyebrow lime">Accounting connection</span>
        <h1>QuickBooks, connected with intention.</h1>
        <p>
          Authorize one QuickBooks Online company securely. Customer, estimate, invoice, and payment
          synchronization stay off until their exact business rules are approved.
        </p>
      </header>

      {message ? (
        <div className="integration-message" role="status">
          <CheckCircle2 /> {message}
        </div>
      ) : null}
      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t complete that QuickBooks action.
          <button onClick={() => void load()}>Refresh status</button>
        </div>
      ) : null}

      <section className="qbo-grid">
        <article className="qbo-connection-card">
          <div className="qbo-mark">
            <Landmark />
          </div>
          <div className="qbo-heading">
            <span className={`qbo-state ${status?.connected ? "connected" : ""}`}>
              {loading ? "Checking" : status?.connected ? "Connected" : "Disconnected"}
            </span>
            <h2>QuickBooks Online</h2>
            <p>
              {status?.enabled
                ? "OAuth is available for an approved administrator. Tokens never enter the browser."
                : "The integration is safely disabled until server-side Intuit credentials are configured."}
            </p>
          </div>
          <dl className="qbo-facts">
            <Fact label="Mode" value={status?.environment ?? "—"} />
            <Fact label="Company / realm" value={status?.realmId ?? "Not connected"} />
            <Fact label="Last sync" value={formatDate(status?.lastSyncAt)} />
            <Fact label="Token status" value={tokenStatus(status)} />
          </dl>
          <div className="qbo-actions">
            {!status?.connected ? (
              <button
                className="primary-button compact-action"
                disabled={!status?.enabled || !canManage || Boolean(action)}
                onClick={() => void connect()}
              >
                {action === "connect" ? <LoaderCircle className="spin" /> : <ExternalLink />}
                Connect QuickBooks
              </button>
            ) : (
              <>
                <button
                  className="primary-button compact-action"
                  disabled={!status.operational || !canManage || Boolean(action)}
                  onClick={() => void testConnection()}
                >
                  {action === "test" ? <LoaderCircle className="spin" /> : <RefreshCw />}
                  Test connection
                </button>
                <button
                  className="secondary-button qbo-disconnect"
                  disabled={!status.enabled || !canManage || Boolean(action)}
                  onClick={() => void disconnect()}
                >
                  {action === "disconnect" ? <LoaderCircle className="spin" /> : <Unplug />}
                  Disconnect
                </button>
              </>
            )}
          </div>
          {!canManage ? (
            <small>Only OWNER and ADMIN roles can change this connection.</small>
          ) : null}
        </article>

        <aside className="qbo-guardrails">
          <span className="eyebrow">Protected by design</span>
          <h3>No accounting automation is active.</h3>
          <ul>
            <li>OAuth state is single-use and expires after ten minutes.</li>
            <li>Access and refresh tokens are encrypted before database storage.</li>
            <li>Token refresh stays server-side and rotates the stored credentials.</li>
            <li>Customer, estimate, invoice, and payment sync flags default to off.</li>
          </ul>
        </aside>
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function tokenStatus(status?: QuickBooksStatus) {
  if (!status?.connected) return "No token stored";
  if (!status.operational) return "Needs configuration";
  return "Encrypted server-side";
}

function formatDate(value?: string | null) {
  if (!value) return "No sync yet";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

function callbackMessage() {
  const result = new URLSearchParams(window.location.search).get("quickbooks");
  if (result === "connected") return "QuickBooks authorization completed successfully.";
  if (result)
    return "QuickBooks did not complete authorization. Review the configuration and try again.";
  return "";
}
